import type {Rule} from 'antd/es/form';

import {CommonErrors} from '../../../constants/Common';
import {
  LCIA_EN_15804_VALUE,
  LCIA_EF_3_1_VALUE,
  LCIA_METHODS,
  PCF_STANDARD_EN_15804_VALUE,
  PCF_STANDARD_PEF_VALUE,
  type PcfSelectOption
} from '../constants/pcfOptions';
import {ImpactConfigurationLabels} from '../PCFKeyLabels';

const BIOGENIC_EN_15804_VALUE = 'include_biogenic_separate';
const DEFAULT_LCIA_VALUE = 'ipcc_2021_gwp100';
const DEFAULT_GWP_HORIZON = 'gwp100';
const DEFAULT_BIOGENIC_VALUE = 'exclude_biogenic';

export const getLciaMethodsForPcfStandard = (
  pcfStandard?: string
): PcfSelectOption[] => {
  if (pcfStandard === PCF_STANDARD_EN_15804_VALUE) {
    return LCIA_METHODS.filter((option) => option.value === LCIA_EN_15804_VALUE);
  }

  if (pcfStandard === PCF_STANDARD_PEF_VALUE) {
    return LCIA_METHODS.filter((option) => option.value === LCIA_EF_3_1_VALUE);
  }

  return LCIA_METHODS.filter((option) => option.value !== LCIA_EN_15804_VALUE);
};

export const getImpactConfigurationPresets = (
  pcfStandard?: string
): Record<string, string> => {
  if (pcfStandard === PCF_STANDARD_EN_15804_VALUE) {
    return {
      lciaMethod: LCIA_EN_15804_VALUE,
      biogenicHandling: BIOGENIC_EN_15804_VALUE
    };
  }

  if (pcfStandard === PCF_STANDARD_PEF_VALUE) {
    return {
      lciaMethod: LCIA_EF_3_1_VALUE
    };
  }

  return {};
};

export const getNonExpertImpactPresets = (
  pcfStandard?: string
): Record<string, string | boolean> => {
  const standardPresets = getImpactConfigurationPresets(pcfStandard);

  return {
    lciaMethod: standardPresets.lciaMethod ?? DEFAULT_LCIA_VALUE,
    gwpHorizon: DEFAULT_GWP_HORIZON,
    biogenicHandling: standardPresets.biogenicHandling ?? DEFAULT_BIOGENIC_VALUE,
    luc: false,
    sequestration: false,
    uncertainty: false
  };
};

export const shouldShowLciaMethodField = (
  expertMode: boolean,
  pcfStandard?: string
): boolean =>
  expertMode ||
  pcfStandard === PCF_STANDARD_EN_15804_VALUE ||
  pcfStandard === PCF_STANDARD_PEF_VALUE;

export const shouldShowBiogenicField = (
  expertMode: boolean,
  pcfStandard?: string
): boolean =>
  expertMode || pcfStandard === PCF_STANDARD_EN_15804_VALUE;

export const lciaMethodRules = (pcfStandard?: string): Rule[] => [
  {
    required: true,
    message: CommonErrors.mandatory(ImpactConfigurationLabels.LCIA_METHOD)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || String(value).trim() === '') {
        await Promise.resolve();

        return;
      }

      if (
        pcfStandard === PCF_STANDARD_EN_15804_VALUE &&
        value !== LCIA_EN_15804_VALUE
      ) {
        throw new Error(
          'EN 15804+A2 requires the 19 mandatory impact indicators LCIA method'
        );
      }

      if (pcfStandard === PCF_STANDARD_PEF_VALUE && value !== LCIA_EF_3_1_VALUE) {
        throw new Error('PEF / PEFCR requires the EF 3.1 LCIA method');
      }

      await Promise.resolve();
    }
  }
];
